import React from "react"
import styled from "@emotion/styled"
import gql from "graphql-tag"
import { useQuery } from "@apollo/client"
import { MaxWidth, FlexWrap } from "../UI/Containers"
import stromstad from "../img/stromstad.png"
import { Link } from "react-router-dom"
import { prefixImageUrl } from "../utils/imageUrl"
import LinkedIn from "../img/LinkedIn.js"

const FooterWrapper = styled("footer")`
    text-align: left;
    background: ${props => props.theme.colors.secondary};
    color: #fff;
    position: relative;
`

const FooterLogoWrapper = styled("div")`
    background: ${props => props.theme.colors.accent};
    padding: ${props => props.theme.spacing.default};
`

const ImgHolder = styled("a")`
    filter: grayscale(1);
    transition: 0.3s;
    img {
        max-width: 110px;
        min-width: ${props => (props.type === "svg" ? "90px" : "none")};
        height: auto;
        width: auto;
        margin: 0.75rem;
        max-height: 70px;
        mix-blend-mode: ${props =>
    props.type === "jpg" ? "multiply" : "none"};
        ${props => props.theme.bp.below.sm} {
            max-width: 80px;
            max-height: 40px;
            min-width: ${props => (props.type === "svg" ? "80px" : "none")};
        }
    }
    &:hover {
        filter: none;
    }
`

const LogoFlexWrap = styled(FlexWrap)`
    flex-wrap: wrap;
    justify-content: center;
`

const BusinessInfo = styled("div")`
    padding: ${props => props.theme.spacing.default};
    line-height: 1.5em;
    display: flex;
    justify-items: space-between;
    ${props => props.theme.bp.below.sm} {
        flex-wrap: wrap;
        padding-right: 120px;
    }
    h2 {
        margin-bottom: 1em;
        font-family: ${props => props.theme.fonts.body};
    }
    .footerMenu {
        padding-left: 2rem;
        ${props => props.theme.bp.below.sm} {
            padding-left: 0rem;
            padding-top: 2rem;
        }
    }
    ul {
        padding-left: 0;
        list-style: none;
        a {
            color: white;
        }
    }
`

const StromStadWrapper = styled("div")`
    position: absolute;
    bottom: 2rem;
    right: 2rem;
    max-width: 100px;
    img {
        max-width: 100%;
        height: auto;
        min-width: 80px;
    }
    ${props => props.theme.bp.below.sm} {
        max-width: 80px;
    }
`
const FOOTER_PROPS = gql`
    query footerProps {
        allProperties(footer: true) {
            edges {
                node {
                    title
                    website
                    logo(version: "logo") {
                        url
                    }
                }
            }
        }
    }
`

const FooterLogos = () => {
  const { loading, error, data } = useQuery(FOOTER_PROPS)

  if (loading) return null
  if (error) {
    console.error("Error fetching footer logos:", error)
    return null
  }
  if (!data?.allProperties?.edges?.length) return null

  return (
    <FooterLogoWrapper>
      <MaxWidth>
        <LogoFlexWrap>
          {data.allProperties.edges.map(({ node }, index) => {
            if (!node || !node.logo?.url) return null
            return (
              <ImgHolder
                key={index}
                href={node.website}
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  alt={node.title || 'Logo'}
                  src={prefixImageUrl(node.logo.url)}
                />
              </ImgHolder>
            )
          })}
        </LogoFlexWrap>
      </MaxWidth>
    </FooterLogoWrapper>
  )
}

const FooterMenu = () => {
  const { loading, error, data } = useQuery(menuQuery)

  if (loading) return null
  if (error) {
    console.error("Error fetching footer menu:", error)
    return null
  }
  if (!data?.menu?.items) return null

  return (
    <div className="footerMenu">
      <ul>
        {data.menu.items.map((item, i) => {
          return (
            <li key={i}>
              <Link to={item.url}>{item.title}</Link>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

const FooterContent = props => {
  if (!props.site) return null

  return (
    <FooterWrapper>
      <FooterLogos />

      <BusinessInfo>
        <div>
          <h2>{props.site.name}</h2>
          <span>{props.site.address}</span>
          <br />
          <span>{props.site.zipcode} </span>
          <span>{props.site.city}</span>
          <br />
          <span>Tel: </span>
          <span>{props.site.phone}</span>
          <br />
          <span>Org.nr: </span>
          <span>{props.site.fax}</span>
          <br />
          <br />
          <a
            href={"https://www.linkedin.com/company/orvelin-fastigheter-ab"}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'white', textDecoration: 'underline', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <LinkedIn /> Följ oss på LinkedIn
          </a>

        </div>
        <FooterMenu />
        <StromStadWrapper>
          <img
            src={stromstad}
            alt="Strömstad - Havet i centrum och livet i fokus"
          />
        </StromStadWrapper>
      </BusinessInfo>
    </FooterWrapper>
  )
}

const Footer = () => {
  const { loading, error, data } = useQuery(query)

  if (loading) return <FooterWrapper><div>Loading...</div></FooterWrapper>
  if (error) {
    console.error("Error fetching site data:", error)
    return <FooterWrapper><div>Error loading footer content.</div></FooterWrapper>
  }

  return <FooterContent site={data?.site} />
}

const query = gql`
    {
        site {
            name
            address
            city
            phone
            email
            fax
            zipcode
        }
    }
`

const menuQuery = gql`
    {
        menu(position: "footer") {
            items {
                title
                url
                children {
                    title
                    url
                }
            }
        }
    }
`

export default Footer
